// Chain — what the wallets did, against what the books say.
//
// Operator, 2026-08-22 / 08-24: "onchain deposit/withdrawal history … with auto
// matching to transactions once recorded and manual overrides in case of single
// record matching with multiple onchain tx, ignoring rounding errors and still
// gets auto matched … not recorded still shown as pending, with colour."
//
// COLOUR IS THE WHOLE POINT, so it carries one meaning only:
//   amber   pending  — on chain, not in the books. The only colour that is work.
//   green   auto     — matched by scripts/chain-sync.mjs within tolerance.
//   blue    manual   — the operator matched it; the matcher could not.
//   grey    ignored  — pre-cutover, or not a desk movement. Never deleted.
// A rounded match keeps its residue on the row rather than rounding it away:
// the operator asked for rounding to be TOLERATED, not hidden.
//
// 14 Sept 2026 (sweep items 26 and 34):
//   * the Ignore note and the typed books-tx id are state PER ROW — one shared
//     pair meant a reason typed under row A sat in row B's box with the button
//     enabled;
//   * a typed id is RESOLVED before Match is enabled, and the row says what it
//     is ("tx 482 · Loki · 5,000 USDT · 3 Sep");
//   * Ignore is preset chips + "Other…" — 253 ignores in 30 days, 246 of them
//     one of two pasted sentences;
//   * a tie whose chain total differs from the wallet leg by more than dust
//     says so in words ("chain sent 24,703.55, trade says 24,678.85 — correct
//     the trade?") instead of a bare "grouped";
//   * a row dated before the 1 Sept opening is never tied to a posting — it is
//     parked as inside the opening, and the screen offers only that.
//
// 16 Sept 2026: a row the BFF names as an exchange movement (r.venue — the
// venue's own record met it by hash, deposit address + amount, or withdrawal
// amount ± fee within 6 h) offers "Move to Bitget →" / "Move from KuCoin →"
// first in its row: Move money opens with both accounts, the chain's figure
// and time, the venue's fee and the note filled in — one tap to post
// (operator: "how can i select and record the move in 1 click?").
//
// 19 Sept 2026: THE LOOKALIKE (bff/src/routes/chain.ts lookalikeOfSql — one
// rule, in SQL, the matcher's and this screen's). A row whose other end reads
// like a known address without being it — Bitget's or KuCoin's deposit
// address with a different middle, minutes around every real send — wears a
// red "lookalike — poisoning attempt" pill and offers no booking and no tie:
// never a party, never a tie. The other end is on every row now, shortened,
// with the FULL address in the tooltip and one tap to copy it whole — a
// shortened address is exactly what the poisoning imitates, so the copy
// never comes from the screen's shortening.
//
// 14 Sept 2026 (SWEEP-SCOPE §5.27): the screen is "Chain"; the book chips
// use the form's own words ("Book trade · Customer sells" opens a form that
// reads Customer sells — the old "Book buy — desk bought these coins" was
// desk-side); the colour meaning, the auto-sync cadence and the two sentences
// about several sends against one entry live in "About this screen"; the
// helper sentences are one line; the row header wraps on a phone so the time
// stays on the card.
//
// 21 Oct 2026 audit (overnight clean view; operator: "I need clean view for
// operator"). Order, labels and repeats only — no door was removed, none goes
// anywhere else, no match is offered that was not, no guard was loosened:
//   * an opened pending row leads with what it can be MATCHED to (the suggested
//     matches, the sets one send would cover, the tie-by-id door); the booking
//     doors follow, every one still there. On a row already booked from here
//     the line that says so is now the first thing under the row, above the doors;
//   * the Pending tab's rows no longer each wear a "Pending" pill — the lit tab
//     says it (showsStatus); All still shows every state, with colour;
//   * a row dated after the opening is no longer offered "Pre-cutover" or
//     "Backlog ruling" (neither can be true of it; the reasons' words and values
//     are untouched);
//   * a look-alike's warning is said by its pill and its red line — the third
//     paragraph that repeated them is gone, the one fact it alone held stays;
//   * the helper sentences inside an opened row became labels ("Suggested
//     matches", "Tie by books tx id"); their words live in About this screen;
//   * the tabs wrap on a phone again, so "All" is on screen (the sideways strip
//     hid it at 390 px and below), and the row header wraps, so a look-alike's
//     long pill no longer pushes the time off the card.
//
// 22 Oct 2026 design-system sweep (operator: "a human is going to use them"): every failed read is ONE red line with its
// words a tap away and a Try again; the empty states are one line; a closed row says WHO and HOW MANY matches, and keeps the
// matcher's guard sentences whole and amber (see noteParts) instead of a two-line clamp that cut them off; the other end is the
// kit's Address (first eight, last four — a look-alike no longer prints as its twin); the doors are labels, their explanations
// a tooltip and About; what he presses is body size and 44 px on a phone. No door was removed, none goes anywhere else.
import { useState, useEffect, useRef } from 'react';
import { errorWords, ApiError, readWords } from '../lib/errors';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, request, type ChainRow, type ChainCandidate } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { type PillTone } from '../components/Pill';
import { Pending, Pill, StateLine, EmptyState, Address } from '../components/kit';
import { fmtAmount, fmtRelative, fmtDate, fmtDay, displayAsset, isoToDubaiWall } from '../lib/format';
import { partyName, dustFor, sameAsset, railName, brandCase } from '../lib/naming';
import { sameId } from '../lib/ids';
import { foldPoison, hiddenWords, isPoisonRow } from '../lib/chain-poison';
import { overTiedLines, type MarkResult as MarkResultV2 } from '../components/v2/chain/rules';

const TABS = ['pending', 'auto', 'manual', 'ignored', 'all'] as const;

/** WHAT HE PRESSES (design system §1, §4): body size, a 44 px target on a phone, centred. The sweep's one phone-target
 *  string for this group of screens — kit.tsx may take it over. */
const TOUCH = 'text-body py-1.5 px-3 inline-flex items-center justify-center max-lg:min-h-11';

/** THE V3 OPENING — the same instant bff/src/routes/chain.ts refuses to tie
 *  across. A row dated before it already sits inside the 1 Sept opening
 *  balance; the only honest thing to do with it here is park it. */
const V3_OPEN_MS = Date.parse('2026-08-31T20:00:00Z');   // 2026-09-01 00:00 Dubai

/** POST /api/chain/sync answers with `spoofed` (bff/src/routes/chain.ts) but
 *  the client's return type in lib/api.ts does not list it yet, and that file
 *  belongs to another workstream this build. Delete this once it does. */
function spoofedCount(sync: unknown): number {
  const n = (sync as { spoofed?: unknown }).spoofed;
  return typeof n === 'number' ? n : 0;
}
/** The previous read's count (null on the very first read). The route counts
 *  over the explorer's whole window, so the same old poisoning is in every
 *  answer; only a RISE is news (6 Sept 2026). */
function spoofedPrev(sync: unknown): number | null {
  const n = (sync as { spoofed_prev?: unknown }).spoofed_prev;
  return typeof n === 'number' ? n : null;
}

/** BOTH SIDES OF A TIE, sent by the list route since 14 Sept 2026 (sweep item
 *  26): the wallet leg of the posting a row is tied to and the chain's total
 *  against that posting. lib/api.ts (another workstream's file) does not
 *  declare them yet; read off the row until it does. */
type TieRow = ChainRow & { leg_amount?: number | null; chain_sum?: number | null; leg_kind?: string | null;
  /** Whose address the other end imitates (the lookalike rule, 19 Sept 2026) —
   *  the owner's or venue's name, never an address; null when it is not one.
   *  Sent by GET /api/chain; read off the row until lib/api.ts declares it. */
  lookalike_of?: string | null };
/** What PATCH /api/chain/:id answers for a hand match — the same two figures. */
type MarkResult = MarkResultV2;

/** THE MATCHER'S NOTE, READ FOR A CLOSED ROW (bff/src/routes/chain.ts pendingNote — its clauses joined by " · ").
 *
 *  The note used to be printed whole under a two-line clamp: on a phone the guard sentences it carries ("ALREADY BOOKED 2 times
 *  from this row … Keep one, reverse the rest, then Match it") sat behind the ellipsis, and the candidate list — which the opened
 *  row shows as DATA, with a Match each — filled the line. Now:
 *    · the LAST clause is always the candidate list ("2 candidates: tx 5120 … (…, exact) · tx 5109 …") or "no candidates — nothing
 *      booked into … within 30 days that is not already tied": the closed row says only how many ("2 candidates, 1 exact" — and
 *      "3+ exact" when the list is only the nearest three or was cut, see candidateWords). Both are shown in full, as data, when the
 *      row opens (Candidates), and the 30-day rule is in About;
 *    · a clause that stops a wrong booking — already booked, received then forwarded, part of a pass-through, a bucket sum — is
 *      returned apart, WHOLE, and drawn in amber without a clamp;
 *    · "Would cover N postings together …" is the structured set the opened row offers (CoversTie), so the closed row says
 *      "covers N postings" from r.covers and not from the prose;
 *    · every other clause (who, the own-wallet move) stays as it is.
 *  Anything the grammar does not recognise is kept: a clause is only ever folded when it is one of the two known last clauses. */
const GUARD_CLAUSE = /^(ALREADY BOOKED|Already booked|Received then forwarded|Part of a pass-through|With #)/;
export function noteParts(note: string, coversN = 0): { plain: string; guards: string[] } {
  const clauses = note.split(' · ');
  const at = clauses.findIndex((c) => /^(\d+ candidates?(, nearest 3)?: |no candidates — )/.test(c));
  const kept = at < 0 ? clauses : clauses.slice(0, at);
  const plain: string[] = [];
  const guards: string[] = [];
  for (const c of kept) {
    if (GUARD_CLAUSE.test(c)) guards.push(c);
    else if (/^Would cover \d+ postings together/.test(c)) plain.push(`covers ${coversN || c.replace(/^Would cover (\d+).*$/, '$1')} postings`);
    else plain.push(c);
  }
  if (at >= 0) plain.push(candidateWords(clauses.slice(at)));
  return { plain: plain.join(' · '), guards };
}

/** "2 candidates, 1 exact" from the matcher's candidate clause — and never more than that clause can prove (Astra, 22 Oct).
 *
 *  The BFF writes the NEAREST THREE of N ("5 candidates, nearest 3: …", the closest first, so exact matches come first) and cuts a
 *  note at 1,000 characters — the entries in the note are not all the candidates, and the last one may be cut mid-way. Of the
 *  entries that are whole (they end in their gap words: "exact", "lacks … more", "is … short of this"), k are exact out of c:
 *    · k < c — a whole entry that is NOT exact stands, and nothing after it can be (nearest first): "k exact" is complete;
 *    · k = c — every one read is exact, so a candidate beyond the list (N > entries) or a cut entry may be too: "k+ exact", a
 *      lower bound, never "k exact" (four exact candidates read "3 exact");
 *    · k = 0 — nothing is said (the first whole entry not exact means none is; nothing whole means nothing is known). */
function candidateWords(tail: string[]): string {
  const head = tail[0] ?? '';
  const n = /^(\d+) candidates?/.exec(head);
  if (!n) return 'no candidates';
  const total = Number(n[1]);
  const word = `${n[1]} candidate${n[1] === '1' ? '' : 's'}`;
  const entries = [head.replace(/^\d+ candidates?(, nearest 3)?: /, ''), ...tail.slice(1)].filter((e) => /^tx \d+/.test(e));
  const whole = entries.filter((e) => /, (exact|lacks [^()]*more|is [^()]*short of this)\)$/.test(e));
  const k = whole.filter((e) => /, exact\)$/.test(e)).length;
  if (k === 0) return word;
  const more = total > entries.length || whole.length < entries.length;     // candidates beyond the list, or an entry the cut ended inside
  return k < whole.length || !more ? `${word}, ${k} exact` : `${word}, ${k}+ exact`;
}

/** "chain sent 24,703.55, trade says 24,678.85 — correct the trade?" when the
 *  two sides of a tie differ by more than the asset's dust; null when they
 *  agree or either side is unknown. The one sentence, for the row and for the
 *  answer to a fresh match. */
function tieWords(t: { leg_amount?: number | null; chain_sum?: number | null; leg_kind?: string | null; asset: string }): string | null {
  if (t.leg_amount == null || t.chain_sum == null) return null;
  if (Math.abs(t.leg_amount - t.chain_sum) <= dustFor(t.asset)) return null;
  const trade = t.leg_kind === 'trade';
  return `chain sent ${fmtAmount(t.chain_sum, t.asset)}, ${trade ? 'trade says' : 'books say'} ${fmtAmount(t.leg_amount, t.asset)} — correct the ${trade ? 'trade' : 'entry'}?`;
}

/** GET /api/chain/txn/:txn — one books transaction in the operator's words. Through lib/api.ts's one client (sign-in, the refusal in words, the
 *  bound); it goes the moment api.chainTxn exists. */
type ChainTxn = {
  txn: number; timestamp: string; reference: string | null; kind: string | null;
  wallet_slug: string | null; direction: 'in' | 'out' | null; asset: string | null; amount: number | null;
  party_slug: string | null; party_name: string | null;
};
async function readChainTxn(txn: number): Promise<ChainTxn> {
  return request<ChainTxn>(`/api/chain/txn/${txn}`);
}

/** IGNORE, AS CHIPS (sweep item 34). 253 ignores in 30 days: 208 were one
 *  pasted sentence, 38 another. The note that lands on the row is the whole
 *  sentence, so an audit six months on reads a reason, not a label. */
const IGNORE_REASONS: ReadonlyArray<{ label: string; note: string }> = [
  { label: 'Pre-cutover',          note: 'Pre-cutover — inside the 1 September opening balance; nothing in these books to tie it to' },
  { label: 'Backlog ruling',       note: 'Backlog — operator ruling 2026-08-24: chain reconciliation starts that day' },
  { label: 'Dust / spam token',    note: 'Dust or a spam token — not a desk movement' },
  { label: "Not the desk's move",  note: "Not the desk's move — a transfer the desk neither sent nor was paid" },
];

/** The status pill: the state as a word, the tone only tints it. */
const STATUS: Record<string, { word: string; tone: PillTone }> = {
  pending: { word: 'Pending', tone: 'warn' },
  auto:    { word: 'Auto',    tone: 'good' },
  manual:  { word: 'Manual',  tone: 'info' },
  ignored: { word: 'Ignored', tone: 'neutral' },
};

/** WHEN A ROW WEARS ITS STATE (21 Oct 2026 audit). On a one-state tab every row
 *  is that state and the lit chip already says it — twelve amber "Pending"
 *  pills said nothing. The pill is for All, and for any row whose state is not
 *  its tab's (never hide a state that differs from what the tab promised). All
 *  still shows every pending row as pending, with colour (operator, 22/24 Aug:
 *  "not recorded still shown as pending, with colour"). The look-alike pill is
 *  another pill: it always shows. */
const showsStatus = (tab: string, status: string): boolean => tab === 'all' || status !== tab;

export function ChainActivity() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]>('pending');
  const [open, setOpen] = useState<number | null>(null);
  const [openDays, setOpenDays] = useState<ReadonlySet<string>>(new Set());
  // ONE CLICK FROM HOME (operator, 14 Sept 2026: "chain tx not clickable
  // from the dashboard — 1 additional unnecessary click every time"). Home's
  // Chain rows link here with ?row=<transfer id>; that row opens expanded and
  // scrolls into view, its Book trade / Record money / Cheque deposit links
  // right there. Looked for on the tab the screen opened on (Pending, where
  // Home's rows live); a row that has moved on since is found under All.
  const [search] = useSearchParams();
  const rowParam = Number(search.get('row'));
  const rowWanted = Number.isInteger(rowParam) && rowParam > 0 ? rowParam : null;
  const [landedFor, setLandedFor] = useState<number | null>(null);
  // WHAT THE LAST HAND MATCH SAID. A matched row leaves the Pending tab on
  // the refetch, so the sentence about its figures would vanish with it —
  // this line keeps it on screen until the next action.
  const [lastTie, setLastTie] = useState<{ id: number; words: string | null; over: string[] } | null>(null);

  const q = useQuery({ queryKey: ['chain', tab], queryFn: () => api.chainActivity(tab), staleTime: 0 });
  const sync = useMutation({
    mutationFn: () => api.chainSync(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chain'] }),
  });

  // AUTO-SYNC ON ARRIVAL (operator, 1 Sept: booked a row's trade, row stayed
  // pending — "do I need to tell u this really?"). Opening this screen with a
  // read older than 3 minutes pulls the chains and re-matches by itself, once;
  // the button stays for watching a transfer land in real time.
  const autoSynced = useRef(false);
  useEffect(() => {
    if (autoSynced.current || sync.isPending || !q.data) return;
    const at = q.data.chain_synced_at ? Date.parse(q.data.chain_synced_at) : 0;
    if (Date.now() - at > 3 * 60_000) {
      autoSynced.current = true;
      sync.mutate();
    }
  }, [q.data, sync]);
  const rows = (q.data?.rows ?? []) as TieRow[];
  useEffect(() => {
    if (rowWanted === null || landedFor === rowWanted || !q.data) return;
    const here = (q.data.rows as TieRow[]).find((r) => sameId(r.id, rowWanted));
    if (here) { setLandedFor(rowWanted); setOpen(here.id); return; }
    if (tab !== 'all') setTab('all');
    else setLandedFor(rowWanted);            // on no tab at all: nothing to open
  }, [rowWanted, landedFor, q.data, tab]);
  const mark = useMutation({
    // `ties` travels too (db/0061). The first cut widened the prop type and
    // left this body rebuilding the request without it — both split controls
    // sent {action:'match'} alone, and the route refused. The mechanism was
    // there; the path did not reach it (Bookkeeping's review, 30 Sept 2026).
    mutationFn: (v: { id: number; action: 'match' | 'ignore' | 'reopen'; ledger_txn_id?: number; ties?: Array<{ ledger_txn_id: number }>; note?: string }) =>
      api.chainMark(v.id, { action: v.action, ledger_txn_id: v.ledger_txn_id, ties: v.ties, note: v.note }) as Promise<MarkResult>,
    onSuccess: (data, v) => {
      qc.invalidateQueries({ queryKey: ['chain'] });
      setOpen(null);
      const row = rows.find((r) => r.id === v.id);
      const words = v.action === 'match' && row ? tieWords({ ...data, asset: row.asset }) : null;
      const over = v.action === 'match' ? overTiedLines(data) : [];
      setLastTie(words || over.length ? { id: v.id, words, over } : null);
    },
  });

  const counts = q.data?.counts ?? {};

  return (
    <div className="space-y-4">
      <ScreenHeader
        title="Chain"
        subtitle="Wallet transfers against the books."
        help={
          <>
            <p>
              Every transfer the registered wallets made, matched against the books. On All, each
              row's pill says its state: amber is pending — on chain, not in the books — and the
              only colour that is work; green was matched by itself; blue was matched by hand;
              grey is ignored, with its reason, and never deleted.
            </p>
            <p>
              The chains are read every 10 minutes, and again on opening this screen when the last
              read is older than 3 minutes. Refresh reads them now. After booking from a row,
              Refresh — it matches by itself.
            </p>
            <p>
              Suggested matches are the books entries a transfer could be all or part of — same
              wallet, same direction, same coin, within 30 days — nearest first. Several transfers
              may point at one entry — tie each one to the same entry; a sale that arrived in two
              sends shows its remainder there. An entry the list does not show can be tied by its
              books tx id — several, comma-separated, when one send covers them all.
            </p>
            <p>
              The doors on an opened row: Book trade opens the trade form with this transfer&rsquo;s figures; Money in or
              Money out is a receipt or a payment — or a move between your own accounts; Cheque deposit is a
              customer&rsquo;s deposit on a cheque; Delivery charge is a charge paid in coin. A transfer from before the
              1 September opening is already inside the opening balance — park it.
            </p>
          </>
        }
        actions={
        // The read time beside the button on the phone (it had a line of
        // its own under the title, 20 Sept 2026); under it at lg+.
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 lg:flex-col lg:items-end lg:gap-1 lg:shrink-0">
          {/* This button PULLS — it calls the chains, not just the cache. The
              scheduled job does the same every 10 minutes; this is for when the
              operator is watching a transfer land and does not want to wait. */}
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending || q.isFetching}
            className={`btn-secondary ${TOUCH} disabled:opacity-50`}
          >
            {sync.isPending ? 'Reading…' : 'Refresh'}
          </button>
          <span className="text-meta text-tg-hint">
            {q.data?.chain_synced_at
              ? <>Read <span className="font-mono tabular">{fmtRelative(q.data.chain_synced_at)}</span></>
              : 'Not read yet'}
          </span>
          {/* The sync's answer, a line each (22 Oct 2026: one 25-word line). What was read is grey; what was NOT read — a chain the
              reader could not reach, a wallet on a chain it cannot read — and the fake-token count are amber, because a transfer
              on a chain that was not read is not in the list above. */}
          {sync.data && (
            <span className="basis-full text-meta text-tg-hint lg:basis-auto lg:text-right lg:max-w-[22rem]">
              {sync.data.seen} seen · {sync.data.matched} matched
            </span>
          )}
          {sync.data && sync.data.unreachable.length > 0 && (
            <span className="basis-full text-meta text-warn lg:basis-auto lg:text-right lg:max-w-[22rem]">{sync.data.unreachable.length} unreachable</span>
          )}
          {sync.data && sync.data.unsupported.length > 0 && (
            <span className="basis-full text-meta text-warn lg:basis-auto lg:text-right lg:max-w-[22rem]">
              {sync.data.unsupported.length} wallet{sync.data.unsupported.length > 1 ? 's' : ''} on a chain this cannot read
            </span>
          )}
          {/* The route counts spoofed tokens rather than swallowing them —
              a jump means a fresh poisoning campaign against a desk address,
              which is the operator's business (bff/src/routes/chain.ts).
              Read off the response rather than through the client type,
              because lib/api.ts is another workstream's file this build;
              drop the cast when `spoofed` is declared there. */}
          {sync.data && spoofedCount(sync.data) > 0 && (
            spoofedPrev(sync.data) !== null && spoofedCount(sync.data) <= (spoofedPrev(sync.data) ?? 0)
              ? <span className="basis-full text-meta text-tg-hint lg:basis-auto lg:text-right lg:max-w-[22rem]">{spoofedCount(sync.data)} fake-token transfers skipped, none new</span>
              : <span className="basis-full text-meta text-warn lg:basis-auto lg:text-right lg:max-w-[22rem]">{spoofedCount(sync.data)} fake-token transfers skipped{spoofedPrev(sync.data) !== null ? `, ${spoofedCount(sync.data) - (spoofedPrev(sync.data) ?? 0)} new` : ''}</span>
          )}
          {/* "Sync failed" told the operator nothing and gave them nothing to do. One red line, the refusal's sentence a tap away. */}
          {sync.isError && (
            <StateLine tone="bad" className="basis-full lg:basis-auto lg:max-w-[22rem]" more={readWords(sync.error)}>Couldn&apos;t read the chains.</StateLine>
          )}
        </div>
        }
      />

      {/* The tabs were ONE STRIP that scrolled sideways on the phone (the
          PeriodChips shape, 20 Sept 2026) — five tabs wrapped with "All" alone
          on a second line.
          21 Oct 2026 audit: that put "All" past the edge with nothing to say it
          was there — the counts (Auto 231, Ignored 253) filled the 390 px line,
          and a peeking chip shows only between 391 and 428 px (at 375 the four
          chips end 2 px inside the edge and the strip looks complete); a row
          Home linked to that is not under Pending switches to All, which was
          then lit off screen. They wrap again: all five on screen at any width
          and any size of count, "All" on a second line being the price. */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t} type="button" onClick={() => setTab(t)}
            className={`chip ${t === tab ? 'chip-on' : ''}`}
          >
            {t[0]!.toUpperCase() + t.slice(1)}
            {counts[t] && <span className="ml-1.5 text-tg-hint">{counts[t]!.n}</span>}
          </button>
        ))}
      </div>

      {/* A hand match whose two sides disagree: ONE amber line; the figures — "chain sent …, trade says … — correct the trade?" —
          are a tap away, and stay on the tied row itself (Manual) in amber for good. */}
      {/* MORE TIED THAN THE ENTRY HOLDS (over_tied, 4 Nov 2026): said at the tie, in amber — the tie stands. */}
      {lastTie && lastTie.over.length > 0 && (
        <StateLine tone="warn" more={lastTie.over.length > 1 ? lastTie.over.slice(1).join(' · ') : undefined}
          action={<button type="button" className="btn-link text-meta" onClick={() => setLastTie(null)}>dismiss</button>}>
          {lastTie.over[0]}
        </StateLine>
      )}
      {lastTie?.words && (
        <StateLine
          tone="warn"
          more={lastTie.words}
          action={<button type="button" className="btn-link text-meta" onClick={() => setLastTie(null)}>dismiss</button>}
        >
          Tied, but the figures disagree.
        </StateLine>
      )}

      {/* A FAILED READ is one red line (amber was the colour of "pending work" on this screen): what failed, Try again, the
          server's sentence a tap away. Never drawn like the all-clear below. */}
      {q.isError && (
        <StateLine
          tone="bad"
          more={readWords(q.error)}
          action={<button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}
        >
          Couldn&apos;t read the chain.
        </StateLine>
      )}
      {/* A read in flight said nothing at all here, which on a dead Docker looks
          exactly like a screen with no work on it. */}
      {q.isLoading && <Pending />}
      {/* The all-clear is only true when the read SUCCEEDED (3 Sept audit). */}
      {!q.isLoading && !q.isError && q.data && rows.length === 0 && (
        <EmptyState>{tab === 'pending' ? 'All matched or explained.' : 'Nothing here.'}</EmptyState>
      )}

      {/* POISONING FOLDS OUT (lib/chain-poison.ts, 4 Nov 2026): per day one line in its place; a tap shows that day's. */}
      <div className="space-y-2">
        {foldPoison(rows).map((g, i) => {
          const row = (r: TieRow) => <Row key={r.id} r={r} open={open === r.id} onOpen={() => setOpen(open === r.id ? null : r.id)} mark={mark} landed={sameId(r.id, rowWanted)} showStatus={showsStatus(tab, r.status)} />;
          if (g.kind === 'shown') return <div key={`rows:${i}`} className="space-y-2">{g.rows.map(row)}</div>;
          const shut = !openDays.has(g.day);
          return (
            <div key={`poison:${g.day}`} className="space-y-2">
              <button type="button" aria-expanded={!shut} data-poison-day={g.day}
                onClick={() => setOpenDays((s) => { const n = new Set(s); if (n.has(g.day)) n.delete(g.day); else n.add(g.day); return n; })}
                className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-3 text-left text-meta text-tg-hint hover:bg-panel-2 lg:min-h-9">
                <span>{shut ? '▸' : '▾'} {hiddenWords(g.rows.length)}</span>
                <span className="font-mono tabular">{fmtDay(g.day)}</span>
              </button>
              {!shut && g.rows.map(row)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** One mutation serves every row, so `variables` is what says WHICH row the
 *  last refusal belongs to. Without it a failed match on one transfer printed
 *  its sentence under whichever row the operator opened next, which reads as
 *  that row having been refused. */
type Mark = {
  mutate: (v: { id: number; action: 'match' | 'ignore' | 'reopen'; ledger_txn_id?: number; ties?: Array<{ ledger_txn_id: number }>; note?: string }) => void;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  variables?: { id: number };
};

function Row({ r, open, onOpen, mark, landed = false, showStatus = true }: { r: TieRow; open: boolean; onOpen: () => void; mark: Mark; landed?: boolean;
  /** The state pill (showsStatus: All, or a state that is not its tab's). Shown unless the screen says otherwise. */
  showStatus?: boolean }) {
  const inbound = r.direction === 'in';
  // The row Home linked to: scrolled into view once it opens, and edged in
  // the accent so the eye finds it among its neighbours.
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (landed && open) el.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [landed, open]);
  const preOpening = Date.parse(r.block_time) < V3_OPEN_MS;
  const words = tieWords(r);
  // A pending row's matcher note is read, not printed whole (see noteParts); every other state's note is one short sentence.
  const parts = r.note ? (r.status === 'pending' ? noteParts(r.note, r.covers?.[0]?.txns.length ?? 0) : { plain: r.note, guards: [] as string[] }) : { plain: '', guards: [] as string[] };
  const plainNote = parts.plain;
  const guards = parts.guards;
  const chainNote = encodeURIComponent('chain ' + r.tx_hash.slice(0, 14));
  const when = encodeURIComponent(isoToDubaiWall(r.block_time));
  const status = STATUS[r.status] ?? { word: r.status, tone: 'neutral' as PillTone };
  // THE VENUE ON THE FAR END (16 Sept 2026; operator: "how can i select and
  // record the move in 1 click?"). When the BFF says this row is an exchange
  // movement (r.venue — one rule, bff/src/routes/chain.ts venueLinkSql), Move
  // money opens with both accounts, the chain's figure and time and the note
  // filled in: an 'out' row is the wallet's deposit onto the venue, an 'in'
  // row the venue's withdrawal back to the wallet — there the venue's fee
  // rides along when it is in the same coin, paid by the From account, so the
  // gross leaves the venue and the chain's net lands on the wallet. The
  // venue account travels as a URL parameter in the rail grammar; the button
  // says the venue's name. The Movements tab builds the same URL from its side.
  const moveUrl = ((): string | null => {
    if (!r.venue || !r.venue_slug) return null;
    const q = new URLSearchParams({
      rail: inbound ? r.venue_slug : r.wallet_slug, railAsset: r.asset,
      to:   inbound ? r.wallet_slug : r.venue_slug, toAsset: r.asset,
      amount: String(r.amount),
      when: isoToDubaiWall(r.block_time),
      note: 'chain ' + r.tx_hash.slice(0, 14),
    });
    if (inbound && r.venue_fee && r.venue_fee > 0 && sameAsset(r.venue_fee_asset, r.asset)) q.set('fee', String(r.venue_fee));
    return `/treasury/rebalance?${q.toString()}`;
  })();
  return (
    <div ref={el} className={`card p-3 ${landed ? 'border-tg-accent/60' : ''}`}>
      {/* THREE LINES, THE SAME ON EVERY ROW (operator, 19 Sept 2026: "fix the
          phone layout for the chain screen too" — the amount used to land on
          line 1 or line 2 depending on how many digits it had, and the note
          was cut at sixty characters):
            1  status · chain · In/Out ……………………………… time
            2  the amount ……………………… from/to the other end
            3  the note, whole, and the short hash. */}
      <button type="button" onClick={onOpen} className="w-full text-left">
        {/* 21 Oct 2026 audit: the line WRAPS (a look-alike's long pill pushed the
            time off the card on a phone), and the state pill is for All only —
            see showsStatus. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          {showStatus && <Pill tone={status.tone}>{status.word}</Pill>}
          {/* THE LOOKALIKE (19 Sept 2026): the other end imitates a known
              address. Red, on every tab — a parked one still says it. */}
          {r.lookalike_of && <Pill tone="bad">lookalike — poisoning attempt</Pill>}
          <span className="text-meta text-tg-hint font-mono tabular">{r.chain}</span>
          {/* In / Out with the sign (§6 "Money direction"): the tone sits on
              the word, the figure is the row's answering one. */}
          <span className={`text-body font-medium ${inbound ? 'text-pos' : 'text-neg'}`}>{inbound ? 'In' : 'Out'}</span>
          <span className="ml-auto text-meta text-tg-hint font-mono tabular whitespace-nowrap">{fmtRelative(r.block_time)}</span>
        </div>
      </button>
      {/* Line 2: the amount (a tap opens the row, like the lines above and
          below it) and, at the right, THE OTHER END (19 Sept 2026): shortened
          on screen, whole in the tooltip, whole on copy — its own control, so
          the copy is a copy and not a click on the row. */}
      {/* 22 Oct 2026: the line WRAPS — a 9-digit figure ("+123,456,789.25 USDT·TRC") and the other end no longer fit one 390 px
          line, and the address ran past the card's edge. The other end is the kit's Address: first eight, last four, whole on
          copy, red when it is a look-alike. */}
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 min-w-0">
        <button type="button" onClick={onOpen} className="amt-key whitespace-nowrap text-left">
          {inbound ? '+' : '−'}{fmtAmount(r.amount, r.asset)} {displayAsset(r.asset)}
        </button>
        {r.counterparty_address && (
          <span className="ml-auto flex max-w-full items-center gap-x-1.5 text-meta text-tg-hint" title={r.counterparty_address}>
            <span>{inbound ? 'from' : 'to'}</span>
            <Address value={r.counterparty_address} lookalike={!!r.lookalike_of} copy={!isPoisonRow(r)} />
          </span>
        )}
      </div>
      {r.lookalike_of && (
        <div className="text-meta text-neg">reads like {r.lookalike_of}&apos;s address but is not it; never pay it</div>
      )}
      {r.status !== 'pending' && r.over_tied && <div className="text-meta text-warn" data-over-tied="">{r.over_tied}</div>}
      <button type="button" onClick={onOpen} className="w-full text-left">
        <div className={`mt-1 text-meta text-tg-hint ${guards.length ? '' : 'line-clamp-2'}`}>
          {/* THE TWO SIDES OF THE TIE, IN WORDS, when they disagree by more
              than dust (sweep item 26) — a hand match used to read as a bare
              "grouped" whatever the figures. Within dust the residue is still
              shown, never rounded away. */}
          {words
            ? <span className="text-warn">{words}</span>
            : r.confidence === 'rounded' && r.delta != null && <>rounded Δ{r.delta.toFixed(6)}</>}
          {plainNote && <>{(words || (r.confidence === 'rounded' && r.delta != null)) ? ' · ' : ''}{plainNote}</>}
          {(words || plainNote || (r.confidence === 'rounded' && r.delta != null)) && ' · '}
          <span className="font-mono tabular">{r.tx_hash.slice(0, 10)}…</span>
          {r.ledger_txn_id != null && <> · books tx {r.ledger_txn_id}</>}
          {/* One send over several postings (db/0061): every posting, with
              the part of the send it took. */}
          {r.ledger_txn_id == null && (r.tie_n ?? 0) > 1 && (
            <> · books tx {(r.tie_txns ?? []).map((t, i) => (
              <span key={t}>{i > 0 && ' + '}{t}{r.tie_amounts?.[i] != null && <> ({fmtAmount(r.tie_amounts[i]!, r.asset)})</>}</span>
            ))}</>
          )}
        </div>
        {/* The matcher's guard sentences, WHOLE and amber, never clamped: "Already booked … Match it, do not book it again" is what
            stops a second booking, and "ALREADY BOOKED 2 times … Keep one, reverse the rest" was behind the ellipsis. */}
        {guards.map((g) => (
          // words:ok — the matcher's guard sentence stops a second booking; shown whole
          <span key={g} className="mt-1 block text-meta text-warn">{g}</span>
        ))}
      </button>

      {open && (
        <div className="mt-3 border-t border-stroke-soft pt-3 space-y-2">
          {r.status === 'pending' ? (
            r.lookalike_of ? (
              <>
                {/* A LOOKALIKE IS NEVER BOOKED AND NEVER TIED (19 Sept 2026):
                    no Book, no Money, no Move, no candidates, no id box —
                    only the park, with the reason on the row. */}
                {/* 21 Oct 2026 audit: the warning is the pill and the red line under
                    the amount; the paragraph that said it a third time ("reads like
                    <venue>'s address but is not it — an address-poisoning attempt") is
                    gone. The one fact only it held — nothing here to book or tie —
                    stays, as a line. */}
                <p className="text-meta text-tg-hint">Nothing here to book or tie — park it:</p>
                <IgnoreChips r={r} mark={mark} only={['Dust / spam token', "Not the desk's move"]} />
              </>
            ) : preOpening ? (
              <>
                {/* BEFORE THE OPENING: nothing in these books to tie it to,
                    and the route refuses a tie in words. Park it. */}
                {/* 22 Oct 2026: twelve words (it was 26). Why — its coins are already inside the opening balance, nothing here
                    to tie it to — is the chip's own tooltip (IGNORE_REASONS), the note it posts, and About. */}
                <p className="text-meta text-tg-hint">
                  Dated {fmtDay(r.block_time)} — before these books opened on 1 September. Park it:
                </p>
                <IgnoreChips r={r} mark={mark} only={['Pre-cutover']} />
              </>
            ) : (
              <>
                {/* 21 Oct 2026 audit — WHAT IT CAN BE MATCHED TO COMES FIRST. A row
                    already booked from here used to show its booking doors above
                    the line that says "Booked from this row already — Match it, do
                    not book it again"; on a phone the first Match sat four buttons
                    down. Order only: this block (the suggested matches with their
                    Match, the sets one send would cover, the tie-by-id door), then
                    the booking doors below it — every door on every row it was on,
                    one tap, the same place; none hidden by what the list holds. */}
                <Candidates r={r} mark={mark} />
                <CoversTie r={r} mark={mark} />
                <TieById r={r} mark={mark} />
                {/* BOOK IT FROM HERE (operator, 1 Sept): the movement's sign
                    decides what it can be — coins IN are a customer selling or
                    a receipt, coins OUT are a customer buying or a payment. One
                    tap lands on the right screen with wallet, asset, amount AND
                    the chain's own time pre-filled (operator, 12 Sept 2026:
                    "time should auto fetch from the chain tx"); after booking,
                    Refresh auto-matches this row to the new entry. &lock=coin
                    (sweep item 26): the coin box on the far screen is the
                    chain's figure and is not to be typed over — the cheque
                    screen honours it today, the trade screen once its owners
                    wire it. The chip's words are the form's own (§5.27). */}
                <div className="flex flex-wrap gap-2">
                  {/* First when the row is a venue movement: the one tap
                      that books it as a move between the desk's own accounts. */}
                  {moveUrl && r.venue && (
                    <Link className={`btn-secondary ${TOUCH}`} to={moveUrl}>
                      {inbound ? `Move from ${brandCase(r.venue)} →` : `Move to ${brandCase(r.venue)} →`}
                    </Link>
                  )}
                  <Link
                    className={`btn-secondary ${TOUCH}`}
                    to={inbound
                      ? `/trade?dir=customer_sell&take=${r.amount}&rail=${r.wallet_slug}&railAsset=${r.asset}&lock=coin&when=${when}&note=${chainNote}`
                      : `/trade?dir=customer_buy&give=${r.amount}&rail=${r.wallet_slug}&railAsset=${r.asset}&lock=coin&when=${when}&note=${chainNote}`}
                  >
                    {inbound ? 'Book trade · Customer sells' : 'Book trade · Customer buys'}
                  </Link>
                  <Link
                    className={`btn-secondary ${TOUCH}`}
                    title={inbound ? 'A receipt, or a move between your own accounts' : 'A payment, or a move between your own accounts'}
                    to={`/record?dir=${inbound ? 'in' : 'out'}&amount=${r.amount}&asset=${r.asset}&rail=${r.wallet_slug}&railAsset=${r.asset}&when=${when}&note=${chainNote}`}
                  >
                    {inbound ? 'Money in' : 'Money out'}
                  </Link>
                  {/* A coin deposit on a manager's cheque (sweep item 31): the
                      cheque screen opens with this wallet and the coin box
                      locked to what the chain brought. */}
                  {inbound && (
                    <Link
                      className={`btn-secondary ${TOUCH}`}
                      title="A customer's deposit on a cheque"
                      to={`/mc?rail=${r.wallet_slug}&railAsset=${r.asset}&take=${r.amount}&lock=coin&note=${chainNote}`}
                    >
                      Cheque deposit
                    </Link>
                  )}
                  {/* A delivery charge paid in coin (2 Oct 2026): Record money's
                      charge form opens with this wallet, the coin and what the
                      chain brought; the receipt it books carries this row's
                      note when the whole transfer is the charge, so Refresh
                      ties it by itself. */}
                  {inbound && (
                    <Link
                      className={`btn-secondary ${TOUCH}`}
                      title="A delivery charge paid in coin"
                      to={`/record?mode=charge&amount=${r.amount}&rail=${r.wallet_slug}&railAsset=${r.asset}&when=${when}&note=${chainNote}`}
                    >
                      Delivery charge
                    </Link>
                  )}
                </div>
                {/* 21 Oct 2026 audit: this branch is only reached by a row dated after the
                    opening, of which "Pre-cutover" (inside the opening balance) and
                    "Backlog ruling" (before 24 Aug) cannot be true — they were offered
                    on a transfer from minutes ago. The reasons' words and values are
                    untouched (they are posted); "Other…" still takes any. The sentence
                    "After booking, Refresh — it matches by itself." is in About this
                    screen. */}
                <IgnoreChips r={r} mark={mark} only={['Dust / spam token', "Not the desk's move"]} />
              </>
            )
          ) : (
            <button type="button" className={`btn-secondary ${TOUCH}`}
              disabled={mark.isPending}
              onClick={() => mark.mutate({ id: r.id, action: 'reopen' })}>
              Reopen as pending
            </button>
          )}
          {mark.isError && mark.variables?.id === r.id && (
            // words:ok — the refusal's own sentence is the reason; shown whole
            <p className="text-neg text-meta mt-1">{errorWords(mark.error)}</p>
          )}
        </div>
      )}
    </div>
  );
}

/** TIE BY BOOKS TX ID — resolved before Match is enabled (sweep item 34).
 *  The old box posted any integer with no lookup. Now the id is read back
 *  from the books as it is typed and the row says what it is; Match is off
 *  until it names a live transaction with a leg on this wallet, this
 *  direction, this asset. State lives here, per row. Folded: used at most
 *  4 times in 30 days against 29 matches through Candidates.
 *
 *  THE RESOLVED POSTING IS THE CONTROL (operator, 1 Oct 2026, with 4794
 *  typed and "tx 4794 · Ali Zaib · 3,56,600 USDT · 21 Sep" showing under the
 *  box: "see it's suggesting correctly but there is no option to select").
 *  The control was there — a small "Match" up beside the input — and it was
 *  live; but the line read as a suggestion with nothing to tap, and the row
 *  carried a second, identical "Match" on an unrelated candidate card he
 *  had just been told not to press. So the button now sits UNDER the lines
 *  it acts on, exists only once every id has resolved to a leg that fits,
 *  and says what it will do in the posting's own words — it cannot be
 *  mistaken for a candidate's. Same mutation, same request. */
function TieById({ r, mark }: { r: TieRow; mark: Mark }) {
  const [txn, setTxn] = useState('');
  // A short pause after the last keystroke, so "48" on the way to "482" is
  // not looked up as its own id.
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSettled(txn.trim()), 350);
    return () => clearTimeout(t);
  }, [txn]);
  // ONE ID OR SEVERAL (db/0061, 30 Sept 2026). "4944, 4945" — or "4944 +
  // 4945", the way the matcher's own note writes it — ties the send to both,
  // each posting taking what it still lacks; the route refuses in words if
  // the parts do not make the send. This door took one integer while the
  // note above it said "tie by hand" about two. Every id is looked up, and
  // Match is off until every one names a live posting with a leg on this
  // wallet, this way, in this coin.
  const ids = [...new Set(settled.split(/[\s,+]+/).filter(Boolean))];
  const valid = ids.length > 0 && ids.every((s) => /^\d+$/.test(s) && Number(s) > 0);
  const nums = valid ? ids.map(Number) : [];
  const looks = useQueries({
    queries: nums.map((id) => ({
      queryKey: ['chain', 'txn', id],
      queryFn: () => readChainTxn(id),
      retry: false,
      staleTime: 60_000,
    })),
  });
  const found = looks.map((q, i) => (q.data && q.data.txn === nums[i] ? q.data : null));
  // The leg must be on THIS wallet, THIS way, in THIS asset — the route
  // refuses anything else, so the button does not offer it.
  const fitsOne = (t: ChainTxn) => t.wallet_slug === r.wallet_slug && t.direction === r.direction && !!t.asset && sameAsset(t.asset, r.asset);
  const allFit = valid && found.length > 0 && found.every((t) => !!t && fitsOne(t));
  const fetching = looks.some((q) => q.isFetching);
  const submit = () => (nums.length === 1
    ? mark.mutate({ id: r.id, action: 'match', ledger_txn_id: nums[0]! })
    : mark.mutate({ id: r.id, action: 'match', ties: nums.map((n) => ({ ledger_txn_id: n })) }));
  // Whose posting it is, in the words the candidates list uses.
  const whoOf = (t: ChainTxn) => (t.party_slug
    ? partyName({ display_name: t.party_name, slug: t.party_slug })
    : (KIND_WORDS[t.kind ?? ''] ?? t.kind ?? 'Entry'));
  // What the button will do, named by the posting(s) it ties to.
  const tieWordsFor = nums.length === 1
    ? `Tie to tx ${nums[0]}${found[0] ? ` — ${whoOf(found[0])}` : ''}`
    : `Tie to these ${nums.length} — ${nums.map((n) => `tx ${n}`).join(' + ')}`;
  return (
    <details className="text-meta text-tg-hint">
      {/* 21 Oct 2026 audit: a label — "Not listed? … several, comma-separated, when
          one send covers them all" is in About this screen, and the box's own
          placeholder shows several ids. */}
      <summary className="cursor-pointer select-none max-lg:py-3.5">Tie by books tx id</summary>
      <input className="input mt-2 w-full text-field lg:text-body" placeholder="482 — or 4944, 4945"
        value={txn} onChange={(e) => setTxn(e.target.value)} inputMode="numeric" />
      {valid && fetching && <p className="mt-1"><Pending inline /></p>}
      {nums.map((id, i) => {
        const look = looks[i]!;
        const t = found[i];
        if (look.isError) {
          return (
            // words:ok — a lookup's refusal, shown whole beside the id it is about
            <p key={id} className="mt-1 text-neg">
              {look.error instanceof ApiError && look.error.status === 404
                ? `No live books transaction ${id} — check the id.`
                : `Couldn't read tx ${id} — ${readWords(look.error)}`}
            </p>
          );
        }
        if (!t) return null;
        const fits = fitsOne(t);
        const who = whoOf(t);
        return (
          <p key={id} className={`mt-1 ${fits ? 'text-tg-text' : 'text-warn'}`}>
            <span className="font-mono">tx {t.txn}</span> · {who}
            {t.amount != null && t.asset && <> · {fmtAmount(t.amount, t.asset)} {displayAsset(t.asset)}</>}
            {' · '}{fmtDay(t.timestamp)}
            {t.reference && <> · <span className="font-mono">{t.reference}</span></>}
            {/* The account by its NAME, never its stored key (§6 "Internal
                identifiers"): railName derives the same words Treasury and the
                pickers show for that wallet. */}
            {!fits && (
              <> — {t.wallet_slug
                ? `moves ${t.asset ? displayAsset(t.asset) : 'coins'} ${t.direction === 'in' ? 'into' : 'out of'} ${railName({ slug: t.wallet_slug })}; this row is ${displayAsset(r.asset)} ${r.direction === 'in' ? 'into' : 'out of'} ${r.wallet_slug ? railName({ slug: r.wallet_slug }) : 'a desk wallet'}`
                : 'touches no desk wallet'}. Not this one.</>
            )}
          </p>
        );
      })}
      {/* Only once every id typed is a posting this send can be tied to —
          until then there is nothing to press, rather than a dead button. */}
      {allFit && (
        <button type="button" className={`btn-secondary mt-2 w-full ${TOUCH}`}
          disabled={mark.isPending || txn.trim() !== settled}
          onClick={submit}>
          {tieWordsFor}
        </button>
      )}
    </details>
  );
}

/** ONE SEND, SEVERAL POSTINGS — THE MATCHER'S OWN SET, ONE TAP (db/0061,
 *  30 Sept 2026). The sync names, on a pending row, the postings this send
 *  would cover together, and its note used to end "tie by hand" at a door
 *  that took one id. The set is now data on the row (matcher_covers), and
 *  the button hands it to the route, which gives each posting what it still
 *  lacks. Nothing here computes a part: the figures are the route's. */
function CoversTie({ r, mark }: { r: TieRow; mark: Mark }) {
  const sets = r.covers ?? [];
  if (!sets.length || r.status !== 'pending') return null;
  return (
    <div className="space-y-1.5">
      {sets.map((c, i) => (
        <div key={i} className="flex items-center gap-2 rounded-lg bg-tg-secondary/40 px-2.5 py-1.5">
          <div className="min-w-0 flex-1 text-meta">
            <span className="text-tg-text">Covers {c.txns.length} postings together</span>
            <span className="text-tg-hint">
              {' · '}{c.txns.map((t, j) => `tx ${t}${c.refs[j] ? ' ' + c.refs[j] : ''}${c.parties[j] ? ' (' + c.parties[j] + ')' : ''}`).join(' + ')}
              {' = '}{fmtAmount(Number(c.total), r.asset)} {displayAsset(r.asset)}
            </span>
          </div>
          <button type="button" className={`btn-secondary ${TOUCH} shrink-0`} disabled={mark.isPending}
            onClick={() => mark.mutate({ id: r.id, action: 'match', ties: c.txns.map((t) => ({ ledger_txn_id: t })) })}>
            Tie all {c.txns.length}
          </button>
        </div>
      ))}
    </div>
  );
}

/** IGNORE: one tap on a preset chip, or "Other…" for the box (sweep item 34).
 *  `only` narrows the chips — a pre-opening row is offered the one that is
 *  true of it. State lives here, per row. */
function IgnoreChips({ r, mark, only }: { r: TieRow; mark: Mark; only?: string[] }) {
  const [other, setOther] = useState(false);
  const [note, setNote] = useState('');
  const chips = only ? IGNORE_REASONS.filter((c) => only.includes(c.label)) : IGNORE_REASONS;
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-meta text-tg-hint">Ignore —</span>
        {chips.map((c) => (
          <button key={c.label} type="button" className="btn-secondary text-meta py-1 px-2 max-lg:min-h-11"
            disabled={mark.isPending}
            title={c.note}
            onClick={() => mark.mutate({ id: r.id, action: 'ignore', note: c.note })}>
            {c.label}
          </button>
        ))}
        <button type="button" className={`btn-secondary text-meta py-1 px-2 max-lg:min-h-11${other ? ' bg-tg-secondary' : ''}`}
          disabled={mark.isPending} onClick={() => setOther((v) => !v)}>
          Other…
        </button>
      </div>
      {other && (
        <div className="flex gap-2">
          <input className="input flex-1 text-field lg:text-body" placeholder="Anything to remember"
            value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className={`btn-secondary ${TOUCH}`}
            disabled={note.trim().length < 4 || mark.isPending}
            onClick={() => mark.mutate({ id: r.id, action: 'ignore', note: note.trim() })}>
            Ignore
          </button>
        </div>
      )}
    </div>
  );
}

/** WHAT THE OPERATOR CAN SEE (operator, 2 Sept: "there is no desk tx id
 *  visible to me"). A books tx id lives on no screen, so asking for one asked
 *  for the impossible. Instead: the books entries this transfer COULD be —
 *  same wallet, same direction, same coin, within 30 days — nearest amount
 *  first, each with its party, Dubai time and reference. One tap ties them.
 *  A booking partly tied to earlier sends stays listed and says what it
 *  still lacks: one booking may cover several physical sends. A booking
 *  already covered, or smaller than the send, is not offered (19 Sept). */
const KIND_WORDS: Record<string, string> = {
  trade: 'Trade', transfer: 'Transfer', mc_pay_seller: 'Cheque · seller paid',
  mc_deliver: 'Cheque · delivered', mc_book: 'Cheque · deposit',
  opening_balance: 'Opening balance', party_payment_split: 'Payment, split',
  // A wallet-paid expense or drawing is a send like any other and has been a
  // candidate since 23 Sept 2026 — it needs its own word here or the list
  // would show the books' own code.
  expense: 'Expense', equity_drawing: 'Drawing', drawing: 'Drawing',
  adjustment: 'Adjustment', chain_swap: 'Chain swap', interco: 'Inter-entity',
};

function Candidates({ r, mark }: { r: ChainRow; mark: Mark }) {
  const q = useQuery({
    queryKey: ['chain', 'candidates', r.id],
    queryFn: () => api.chainCandidates(r.id),
    staleTime: 0,
  });
  /** `by_note`: this entry's own note carries this row's hash — the operator
   *  booked it FROM this row (BFF, 23 Sept 2026). lib/api.ts is another
   *  workstream's file this build; read it off the row until it declares it. */
  const list = (q.data?.candidates ?? []) as Array<ChainCandidate & { by_note?: boolean }>;
  const named = list.find((c) => c.by_note);
  if (q.isLoading) return <p className="text-meta text-tg-hint"><Pending inline /></p>;
  // Naming the cause matters here: "couldn't read the books" alone gives the
  // operator nothing to act on, and a dead Docker looks the same as an empty list.
  if (q.isError) {
    return (
      <StateLine
        tone="bad"
        more={readWords(q.error)}
        action={<button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}
      >
        Couldn&apos;t read the books for candidates.
      </StateLine>
    );
  }
  // The same set the row's note counts (one rule in the BFF, 19 Sept 2026):
  // postings this transfer could be all or part of — never a booking already
  // covered, never one smaller than the send. Empty means book it.
  if (list.length === 0) {
    // 21 Oct 2026 audit: one line. It was "Nothing booked into this wallet in
    // USDT·TRC within 30 days that this receipt could be all or part of — book it
    // above, then Refresh." The window and the rule are in About this screen; and
    // the booking doors now sit BELOW this block, so no direction word.
    return <p className="text-meta text-tg-hint">Nothing near this one — book it, then Refresh.</p>;
  }
  return (
    <div className="space-y-1.5">
      {/* "Booked from this row already — Match it, do not book it again" is a
          GUARD (a second booking is a double entry) and stays whole; it is the
          first line of an opened row now, above the booking doors (21 Oct 2026).
          The plain label was "Could be all or part of one of these, nearest
          first:" — those words are in About this screen. */}
      <p className="text-meta text-tg-hint">
        {named
          ? 'Booked from this row already — Match it, do not book it again:'
          : 'Suggested matches:'}
      </p>
      {list.map((c) => {
        const off = Math.abs(c.delta) >= 0.005;
        const who = c.party_slug
          ? partyName({ display_name: c.party_name, slug: c.party_slug })
          : (KIND_WORDS[c.kind ?? ''] ?? c.kind ?? 'Entry');
        return (
          <div key={c.txn} className="flex items-center gap-2 rounded-lg bg-tg-secondary/40 px-2.5 py-1.5">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="amt text-body">{fmtAmount(c.amount, c.asset)} {displayAsset(c.asset)}</span>
                {off && (
                  <span className="text-meta text-warn tabular">
                    books {c.delta > 0 ? '+' : '−'}{fmtAmount(Math.abs(c.delta), c.asset)} vs chain
                  </span>
                )}
                {c.by_note && (
                  <span className="text-meta text-pos">booked from this row</span>
                )}
                {c.tied > 0 && (
                  <span className="text-meta text-info">
                    {c.tied} send{c.tied > 1 ? 's' : ''} tied ({fmtAmount(c.tied_sum, c.asset)}) · still lacks {fmtAmount(Math.max(0, c.remaining), c.asset)}
                  </span>
                )}
              </div>
              {/* No `truncate`: the reference and the books tx are what he matches BY,
                  and an ellipsis beside a Match button hid them (Astra, 21 Oct). The
                  line wraps, each id one unbreakable piece. */}
              <div className="text-meta text-tg-hint break-words">
                {who}{' · '}{fmtDate(c.timestamp)}
                {c.reference ? <> · <span className="font-mono whitespace-nowrap">{c.reference}</span></> : null}
                {' · '}<span className="font-mono whitespace-nowrap">tx {c.txn}</span>
              </div>
            </div>
            <button type="button" className={`btn-secondary ${TOUCH} shrink-0`}
              disabled={mark.isPending}
              onClick={() => mark.mutate({ id: r.id, action: 'match', ledger_txn_id: c.txn })}>
              Match
            </button>
          </div>
        );
      })}
    </div>
  );
}
